/**
 * The confirmation step on every destructive action.
 *
 * In place, not a modal and not `window.confirm`. Three reasons, in order of weight:
 *
 * 1. It states the CONSEQUENCE, not "are you sure?" — "every user signing in at that address loses
 *    this client's access" is information; "are you sure?" is a speed bump. The approved mock is built
 *    on that distinction.
 * 2. It renders next to the row it is about, so there is no way to confirm the wrong one. A modal
 *    detached from its row is exactly how that mistake happens, and it is the mistake revert exists to
 *    undo.
 * 3. `window.confirm` — the repo's one existing destructive-confirm caller — cannot show emphasis, and
 *    is a browser-chrome dialog an operator dismisses reflexively.
 *
 * NEW rather than reused: the repo has `ui/dialog.jsx` (a Radix modal) and no inline confirm. A modal
 * would fail reason 2.
 */
interface ConfirmInlineProps {
  /** What will happen if they proceed. Must name the specific thing being removed. */
  consequence: React.ReactNode;
  /** The verb, exactly as it appears on the row's button, so the pair reads as one action. */
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export function ConfirmInline({
  consequence,
  action,
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmInlineProps) {
  return (
    <div
      data-testid="confirm"
      role="alertdialog"
      aria-label={`Confirm ${action}`}
      className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900"
    >
      <p className="mb-2">{consequence}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? `${action}…` : action}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default ConfirmInline;
